import React, { useEffect, useRef } from "react";
import { QueryLoader } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useGetEntityDeltasQuery } from "~/graphql/mainHooks";
import {
  type GetTransferBatchesQueryResult,
  type GetEntitiesGlBatchSyncStatusQueryResult,
  useGetEntitiesGlBatchSyncStatusQuery,
  useGetTransferBatchesQuery,
} from "~/graphql/supergraphHooks";
import { type GlSyncError } from "~/pages/FundAccounting/utils/columnDefs";
import { type SubSyncStatus } from "~/pages/FundAccounting/utils/glSyncStatus";
import { toGlobalId, toFederationGlobalId } from "~/utils/graphql-helpers";
import {
  BatchRowStatus,
  checkTransferEvents,
  populatePositionTransferDateMap,
  Row as UnsyncedRow,
} from "./utils";
import UnsyncedTable from "./index";

type WrapperProps = {
  entityName: string;
  selectedEntityId: number;
  arenaId: number;
  isShowing: boolean;
  onClose: () => void;
  onSync: () => void;
  onTransferLoaded: (count: number) => void;
};

type SetTimeoutType = ReturnType<typeof setTimeout>;

const GlBatchSyncStatusMap = {
  completed: BatchRowStatus.COMPLETE,
  failed: BatchRowStatus.FAILED,
  published: BatchRowStatus.PROCESSING,
  received: BatchRowStatus.READY,
  started: BatchRowStatus.PROCESSING,
  MARKED_FOR_SYNC: BatchRowStatus.PROCESSING,
  SYNC_IN_PROGRESS: BatchRowStatus.PROCESSING,
  SYNC_ENABLED: BatchRowStatus.READY,
  SYNC_DISABLED: BatchRowStatus.NOT_READY,
};

type QueryResult = NonNullable<
  GetTransferBatchesQueryResult["data"]
>["entityTransferGlBatchesDetails"];

type GlBatchSyncStatus = NonNullable<
  GetEntitiesGlBatchSyncStatusQueryResult["data"]
>["entitiesGLBatchSyncStatus"][0]["glBatchSyncStatuses"][0];

const UnsyncedTableWrapper = (props: WrapperProps) => {
  const refetchTimeout = useRef<SetTimeoutType | null>(null);
  const transferBatchResponse = useGetTransferBatchesQuery({
    variables: {
      entityId: props.selectedEntityId,
      arenaId: props.arenaId ? props.arenaId : 0,
      isProcessed: false,
    },

    skip: !props.selectedEntityId || !props.arenaId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const entityResponse = useGetEntityDeltasQuery({
    context: withClientContext("staff", {
      arenaId: props.arenaId ? props.arenaId : 0,
    }),
    variables: {
      entitiesFilters: {
        ids: [toGlobalId("AccountObjectType", props.selectedEntityId)],
      },
      transferOnly: true,
    },
    fetchPolicy: "network-only",
  });

  const globalArenaId = toFederationGlobalId("ArenaObjectType", props.arenaId || -1);
  const { data: glBatchStatusData, stopPolling } = useGetEntitiesGlBatchSyncStatusQuery({
    variables: {
      arenaId: globalArenaId,
      entityIds: [props.selectedEntityId],
    },

    fetchPolicy: "no-cache",
    skip: !props.arenaId || !props.selectedEntityId,
    pollInterval: 5000, // 5 seconds
  });

  const batchStatusMap: Record<string, BatchRowStatus> = {};
  // EQ-4892: keep the raw backend status alongside the coarse BatchRowStatus so the
  // renderer can show the descriptive step ("Consolidating…"/"Loading…") + spinner.
  const rawBatchStatusMap: Record<string, SubSyncStatus> = {};
  const syncErrorMap: Record<string, GlSyncError | null> = {};
  const syncStatus = glBatchStatusData?.entitiesGLBatchSyncStatus;
  if (syncStatus && syncStatus.length > 0) {
    syncStatus[0].glBatchSyncStatuses.forEach((status: GlBatchSyncStatus) => {
      if (status.glBatchId) {
        batchStatusMap[status.glBatchId] =
          status.status != null ? GlBatchSyncStatusMap[status.status] : BatchRowStatus.COMPLETE;
        rawBatchStatusMap[status.glBatchId] = status.status;
        syncErrorMap[status.glBatchId] = status.syncError ?? null;
      }
    });
  }

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const deltaData = entityResponse?.data?.investmentEntities?.edges[0]?.node?.deltas;
  const transferDatePositionMap = deltaData ? populatePositionTransferDateMap(deltaData) : {};

  const { data, refetch } = transferBatchResponse;

  const glBatchData: QueryResult = data
    ? data.entityTransferGlBatchesDetails
    : { unsynced: [], history: [] };

  const transferBatchData: UnsyncedRow[] = glBatchData.unsynced.map((batch) => ({
    batchDate: batch.batchDate || "",
    description: batch.description || "",
    positionIds: batch.positionIds || [],
    status: batchStatusMap[batch.id] || BatchRowStatus.READY,
    rawSyncStatus: rawBatchStatusMap[batch.id] ?? null,
    syncError: syncErrorMap[batch.id] ?? null,
  }));

  // Refetch data when any batch becomes completed
  // Assume Complete status means every gl transactions are synced for the batch
  useEffect(() => {
    const hasCompletedBatch = transferBatchData.some(
      (data) => data.status === BatchRowStatus.COMPLETE,
    );
    props.onTransferLoaded(transferBatchData.length);
    if (hasCompletedBatch) {
      if (refetchTimeout.current) {
        return;
      }
      refetchTimeout.current = setTimeout(() => {
        refetch();
        refetchTimeout.current = null;
      }, 5000);
    } else {
      if (refetchTimeout.current) {
        clearTimeout(refetchTimeout.current);
        refetchTimeout.current = null;
      }
    }
  }, [transferBatchData, refetch]);
  const unsyncedRowData = checkTransferEvents(transferBatchData, transferDatePositionMap);

  return (
    <QueryLoader result={[transferBatchResponse]}>
      <UnsyncedTable {...props} rowData={unsyncedRowData} />
    </QueryLoader>
  );
};

export default UnsyncedTableWrapper;
